'use client'

import { useState, ComponentPropsWithoutRef } from 'react'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism'
import Markdown from 'react-markdown'
import { ReadmeTabsControl } from './ReadmeTabsControl'

interface ReadmeTabsWrapperProps {
  packages: string[]
  readmeContents: Record<string, string>
  activeTab: string
}

export default function ReadmeTabsWrapper({ 
  packages, 
  readmeContents,
}: ReadmeTabsWrapperProps) {
  const [activeTab, setActiveTab] = useState(packages[0] || '')
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <>
      <ReadmeTabsControl 
        packages={packages} 
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isExpanded={isExpanded}
        onExpandChange={setIsExpanded}
      />
      
      <div className="relative">
        {packages.map(pkg => (
          <div
            key={pkg}
            className={`prose prose-slate max-w-none ${activeTab === pkg ? 'block' : 'hidden'}`}
          >
            <div className={`transition-all duration-300 ${isExpanded ? 'max-h-none' : 'max-h-96 overflow-hidden'}`}>
              <div className="p-6">
                <Markdown
                  components={{
                    code(props: ComponentPropsWithoutRef<'code'> & {
                      inline?: boolean
                      node?: any
                    }) {
                      const { className, children, ...rest } = props
                      const match = /language-(\w+)/.exec(className || '')
                      const language = match ? match[1] : ''
                      
                      if (!props.inline && language) {
                        return (
                          <SyntaxHighlighter
                            style={oneDark as any}
                            language={language}
                            PreTag="div"
                            className="rounded-lg !my-4"
                            {...rest}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        )
                      }
                      return (
                        <code className="px-1.5 py-0.5 bg-gray-100 rounded text-sm font-mono text-[#0066CC]" {...rest}>
                          {children}
                        </code>
                      )
                    },
                    a({node, children, href, ...props}) {
                      return (
                        <a 
                          href={href}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#0066CC] hover:underline"
                          {...props}
                        >
                          {children}
                        </a>
                      )
                    },
                    table({node, children, ...props}) {
                      return (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full border border-gray-200" {...props}>
                            {children}
                          </table>
                        </div>
                      )
                    },
                    th({node, children, ...props}) {
                      return (
                        <th className="px-4 py-2 bg-gray-50 border-b border-gray-200 font-semibold text-left" {...props}>
                          {children}
                        </th>
                      )
                    },
                    td({node, children, ...props}) {
                      return (
                        <td className="px-4 py-2 border-b border-gray-200" {...props}>
                          {children}
                        </td>
                      )
                    }
                  }}
                >
                  {readmeContents[pkg] || 'Loading README...'}
                </Markdown>
              </div>
            </div>
            {!isExpanded && (
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>
        ))}
      </div>
    </>
  )
} 